/**
 * Finding more for the render library, through Prop Studio's back end: texture
 * sets from Poly Haven or BlenderKit (baked into sets by the back end's
 * Blender), and HDRIs from Poly Haven or an upload. Each fetched one lands in
 * the library and is handed to `onFetched`. Used by the Photoreal window's
 * Textures and Lighting tabs and by Prop Studio's Floors & lighting.
 */

import React, { useRef, useState } from 'react';
import { Download, ImagePlus, Search } from 'lucide-react';
import {
  BLENDERKIT_MATERIAL_CATEGORIES, BlenderKitMaterial, BlenderKitMaterialCategory, BlenderKitTextureResolution, HdriEntry, PolyHavenHdri, PolyHavenTexture,
  ServiceDownError, TextureSet, fetchBlenderKitMaterial, fetchPolyHaven, fetchPolyHavenTexture, searchBlenderKitMaterials, searchPolyHaven,
  searchPolyHavenTextures, uploadHdri,
} from '../../props/studioApi';
import { DESIGNER_STRINGS as S } from '../strings';
import './renderControls.css';

const P = S.photo;
const TEXTURE_SOURCE_KEY = 'designer.photoRender.textureSource';
type Source = 'polyhaven' | 'blenderkit' | 'blenderkit-free';

const say = (err: unknown) => (err instanceof ServiceDownError ? P.hdriNoBackEnd : err instanceof Error ? err.message : String(err));

/** A result tile: its picture, its name, a download mark. */
const tile = (key: string, thumb: string | null | undefined, label: string, sub: string, onClick: () => void, wide = false, enabled = true) => (
  <div key={key} style={{ position: 'relative' }}>
    <button
      type="button" className={`rc-tile${wide ? ' is-wide' : ''}`} onClick={onClick} title={sub ? `${label} · ${sub}` : label}
      style={{ backgroundImage: thumb ? `url(${thumb})` : undefined }}
    >
      <span className="rc-tile-name">{label} {sub && <span style={{ opacity: 0.7 }}>{sub}</span>}</span>
    </button>
    <Download size={12} style={{ position: 'absolute', top: 4, right: 4, color: enabled ? '#fff' : '#777', pointerEvents: 'none' }} />
  </div>
);

/** The results grid: three to a row in a side panel; `wide` (a modal) as many tiles as fit, the modal scrolling them. */
const gridClass = (wide: boolean | undefined, extra = '') => `rc-grid${extra}${wide ? ' lf-wide' : ' is-scroll'}`;

/**
 * Search Poly Haven or BlenderKit for texture sets; a click fetches one into the library.
 * `blender` false (a back end without Blender, as on a web server): BlenderKit is
 * not offered — its materials are .blend files that only Blender can bake into maps.
 */
export const TextureFinder: React.FC<{ onFetched: (set?: TextureSet) => void; wide?: boolean; blender?: boolean }> = ({ onFetched, wide, blender = true }) => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'' | 'grass' | 'wood'>('');
  const [found, setFound] = useState<PolyHavenTexture[] | null>(null);
  const [resolution, setResolution] = useState<'1k' | '2k' | '4k'>('2k');
  const [chosen, setSource] = useState<Source>(() => { try { return (window.localStorage.getItem(TEXTURE_SOURCE_KEY) as Source | null) || 'polyhaven'; } catch { return 'polyhaven'; } });
  const source: Source = blender ? chosen : 'polyhaven';
  const [foundBk, setFoundBk] = useState<BlenderKitMaterial[] | null>(null);
  const [bkCategory, setBkCategory] = useState<BlenderKitMaterialCategory>('');
  // The next page of BlenderKit results, when there is one (More results).
  const [bkNext, setBkNext] = useState<number | null>(null);
  const chooseSource = (s: Source) => { setSource(s); setFound(null); setFoundBk(null); setBkNext(null); try { window.localStorage.setItem(TEXTURE_SOURCE_KEY, s); } catch { /* not kept — fine */ } };

  const search = async (page = 1) => {
    setBusy('search');
    try {
      if (source === 'polyhaven') {
        setFound(await searchPolyHavenTextures(query.trim(), kind));
      } else {
        const got = await searchBlenderKitMaterials(query.trim(), source === 'blenderkit-free', bkCategory, page);
        setFoundBk((prev) => (page > 1 && prev ? [...prev, ...got.results.filter((m) => !prev.some((p) => p.asset === m.asset))] : got.results));
        setBkNext(got.next);
      }
      setError(null);
    } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };
  const fetchOne = async (id: string) => {
    setBusy(id);
    try {
      const set = await fetchPolyHavenTexture(id, resolution);
      setError(null);
      onFetched(set);
    } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };
  const fetchBk = async (asset: string) => {
    setBusy(asset);
    try {
      const set = await fetchBlenderKitMaterial(asset, `resolution_${resolution.toUpperCase()}` as BlenderKitTextureResolution);
      setError(null);
      onFetched(set);
    } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };
  const bkSub = (m: BlenderKitMaterial) => [m.sizeM ? P.textureSize(m.sizeM / 0.3048, m.sizeM / 0.3048) : '', m.isFree ? P.textureBkFree : P.textureBkPlan].filter(Boolean).join(' · ');

  return (
    <>
      <div className="rc-row">
        <div className="rc-row-head">
          <span className="rc-label" title={P.textureSourceHint}>{P.textureSource}</span>
          <select className="rc-select" aria-label={P.textureSource} title={source === 'polyhaven' ? P.polyHavenTexturesNote : P.blenderKitTexturesNote} value={source} onChange={(e) => chooseSource(e.target.value as Source)}>
            <option value="polyhaven">{P.textureSourcePolyHaven}</option>
            {blender && <option value="blenderkit">{P.textureSourceBlenderKit}</option>}
            {blender && <option value="blenderkit-free">{P.textureSourceBlenderKitFree}</option>}
          </select>
        </div>
        {!blender && <p className="rc-note" style={{ marginTop: 2 }}>{P.textureNoBlender}</p>}
        <div className="rc-row-head" style={{ gap: 4, marginTop: 4 }}>
          <input
            className="rc-select" style={{ flex: 1, minWidth: 0 }} placeholder={source === 'polyhaven' ? P.textureSearchPlaceholder : P.textureSearchBkPlaceholder} aria-label={P.textureSearchPlaceholder}
            value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          />
          {source === 'polyhaven' && (
            <select className="rc-select" style={{ flex: '0 0 auto', width: 90 }} aria-label={P.textureKind} value={kind} onChange={(e) => setKind(e.target.value as '' | 'grass' | 'wood')}>
              <option value="">{P.textureKindAny}</option><option value="grass">{P.textureKindGrass}</option><option value="wood">{P.textureKindWood}</option>
            </select>
          )}
          {source !== 'polyhaven' && (
            <select className="rc-select" style={{ flex: '0 0 auto', width: 90 }} aria-label={P.textureBkCategory} title={P.textureBkCategoryHint} value={bkCategory} onChange={(e) => setBkCategory(e.target.value as BlenderKitMaterialCategory)}>
              <option value="">{P.textureKindAny}</option>
              {BLENDERKIT_MATERIAL_CATEGORIES.map((c) => <option key={c} value={c}>{P.textureBkCategories[c] ?? c}</option>)}
            </select>
          )}
          <select className="rc-select" style={{ flex: '0 0 auto', width: 58 }} aria-label={P.textureResolution} title={P.textureResolutionHint} value={resolution} onChange={(e) => setResolution(e.target.value as '1k' | '2k' | '4k')}>
            <option value="1k">1K</option><option value="2k">2K</option><option value="4k">4K</option>
          </select>
          <button type="button" className="rc-action" style={{ flex: '0 0 auto' }} disabled={busy === 'search'} onClick={() => search()} aria-label={P.textureSearch}><Search size={12} /></button>
        </div>
      </div>
      {found && found.length === 0 && <p className="rc-note">{P.textureNothingFound}</p>}
      {found && found.length > 0 && (
        <div className={gridClass(wide)}>
          {found.map((t) => tile(`ph-${t.id}`, t.thumbnail, busy === t.id ? P.textureFetching : t.name, t.dimensions ? P.textureSize(t.dimensions[0] / 304.8, t.dimensions[1] / 304.8) : '', () => { if (!busy) fetchOne(t.id); }))}
        </div>
      )}
      {foundBk && foundBk.length === 0 && <p className="rc-note">{P.textureNothingFoundBk}</p>}
      {foundBk && foundBk.length > 0 && (
        <div className={gridClass(wide)}>
          {foundBk.map((m) => tile(`bk-${m.asset}`, m.thumbnail, busy === m.asset ? P.textureBaking : m.name, bkSub(m), () => { if (!busy && m.canDownload) fetchBk(m.asset); }, false, m.canDownload))}
        </div>
      )}
      {foundBk && bkNext !== null && (
        <div className="rc-actions" style={{ marginTop: 6 }}>
          <button type="button" className="rc-action" disabled={busy === 'search'} onClick={() => search(bkNext)}>{P.textureMore(foundBk.length)}</button>
        </div>
      )}
      {error && <p className="rc-note is-bad">{error}</p>}
    </>
  );
};

/** Upload an HDRI, or search Poly Haven's; a fetched one lands in the library. */
export const HdriFinder: React.FC<{ onFetched: (entry: HdriEntry) => void; wide?: boolean }> = ({ onFetched, wide }) => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<PolyHavenHdri[] | null>(null);
  const [resolution, setResolution] = useState<'1k' | '2k' | '4k' | '8k'>('2k');
  const fileInput = useRef<HTMLInputElement>(null);

  const search = async () => {
    setBusy('search');
    try { setFound(await searchPolyHaven(query.trim())); setError(null); } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };
  const fetchOne = async (id: string) => {
    setBusy(id);
    try { onFetched(await fetchPolyHaven(id, resolution)); setError(null); } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };
  const upload = async (file: File) => {
    setBusy('upload');
    try { onFetched(await uploadHdri(file)); setError(null); } catch (err) { setError(say(err)); } finally { setBusy(null); }
  };

  return (
    <>
      <div className="rc-actions">
        <button type="button" className="rc-action" disabled={!!busy} onClick={() => fileInput.current?.click()}><ImagePlus size={12} /> {busy === 'upload' ? P.hdriUploading : P.hdriUpload}</button>
        <input
          ref={fileInput} type="file" accept=".hdr,.exr" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) upload(f); }}
        />
      </div>
      <div className="rc-row" style={{ marginTop: 8 }}>
        <div className="rc-row-head" style={{ gap: 4 }}>
          <input
            className="rc-select" style={{ flex: 1, minWidth: 0 }} placeholder={P.hdriSearchPlaceholder} aria-label={P.hdriSearchPlaceholder}
            value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } }}
          />
          <select className="rc-select" style={{ flex: '0 0 auto', width: 58 }} aria-label={P.hdriResolution} title={P.hdriResolutionHint} value={resolution} onChange={(e) => setResolution(e.target.value as '1k' | '2k' | '4k' | '8k')}>
            <option value="1k">1K</option><option value="2k">2K</option><option value="4k">4K</option><option value="8k">8K</option>
          </select>
          <button type="button" className="rc-action" style={{ flex: '0 0 auto' }} disabled={busy === 'search'} onClick={search} aria-label={P.hdriSearch}><Search size={12} /></button>
        </div>
      </div>
      {found && found.length === 0 && <p className="rc-note">{P.hdriNothingFound}</p>}
      {found && found.length > 0 && (
        <div className={gridClass(wide, ' is-2')}>
          {found.map((h) => tile(`ph-${h.id}`, h.thumbnail, busy === h.id ? P.hdriFetching : h.name, '', () => { if (!busy) fetchOne(h.id); }, true))}
        </div>
      )}
      <p className="rc-note">{P.polyHavenNote}</p>
      {error && <p className="rc-note is-bad">{error}</p>}
    </>
  );
};
